import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  Paper,
  Stack,
  TextField,
} from '@mui/material';
import { get } from 'lodash';

import { Select } from '../Select';
import { Options } from '../Select/props';

import { OptionsProps, Props } from './props';
import { QueryRegistry } from './query.registry';
// TODO: support pagination
const SelectWithDataView = <T, K>(props: Props<T, K>) => {
  const {
    label,
    options,
    queryKey,
    selectProps,
    dataAccess,
    hooksOptions,
    refetchQuery,
    postMenuItem,
    skip = false,
    preMenuItem,
    enableSearch = false,
    autoCompleteInputProps,
    autoCompleteTextFieldProps,
    disabledCondition = [],
    onError,
    ...rest
  } = props;
  // ======================= STATE
  const [optionsData, setOptionsData] = useState<Options[]>([]);
  const [isDisabled, setIsDisabled] = useState<boolean | undefined>();

  // ======================= VARIABLES
  const query =
    QueryRegistry?.find((x) => x.name === queryKey)?.hooks ??
    (() => {
      return {
        loading: false,
        refetch: null,
      };
    });
  // ======================= API
  const { loading, refetch } = query({
    ...hooksOptions,
    onCompleted: (data: { [x: string]: any[] }) => {
      const temp: any = [];
      get(data, dataAccess)?.forEach?.((value) => {
        if (!enableSearch) {
          temp.push(
            options?.renderOptions?.(value) ?? {
              label: value?.[options?.['label'] as string],
              value: value?.[options?.['value'] as string],
            }
          );
          return;
        }
        temp.push(
          options?.renderAutoCompleteOptions?.(value) ?? {
            label: value?.[options?.['label'] as string],
            // allow to pass in custom format
            value: value?.[options?.['value'] as string],
          }
        );
      });
      setOptionsData(temp);
    },
    onError: (data: any) => {
      onError?.(data);
      return;
    },
    skip,
  });

  // ======================= EFFECTS
  useEffect(() => {
    if (refetchQuery) {
      refetchQuery(refetch);
    }
  }, [refetch, refetchQuery]);

  useEffect(() => {
    setIsDisabled(disabledCondition?.some((condition) => condition));
  }, [disabledCondition]);

  // ======================= RENDER FUNCTIONS
  const renderSelectType = () => {
    if (enableSearch) {
      return (
        <Autocomplete
          isOptionEqualToValue={(option, value) => {
            return option.value === value.value;
          }}
          {...rest}
          options={optionsData}
          getOptionLabel={(val) => {
            return val.label || '';
          }}
          renderOption={(props, renderOptions) => {
            return (
              <li {...props}>
                {options?.renderOptions?.(renderOptions) || renderOptions.label}
              </li>
            );
          }}
          PaperComponent={({ children, ...other }) => {
            return (
              <Paper {...other} onMouseDown={(event) => event.preventDefault()}>
                {preMenuItem && (
                  <Stack
                    px={4}
                    pt={2}
                    sx={{
                      cursor: 'pointer',
                    }}
                    onClickCapture={(e) => {
                      e?.preventDefault();
                      e.stopPropagation();
                    }}
                    onMouseDown={(event: any) => {
                      preMenuItem?.onClick?.(event);
                    }}
                  >
                    {preMenuItem?.render(optionsData)}
                  </Stack>
                )}
                {children}
                {postMenuItem && (
                  <Stack
                    px={4}
                    pb={2}
                    sx={{
                      cursor: 'pointer',
                    }}
                    onMouseDown={(event: any) => {
                      event.stopPropagation();
                      postMenuItem?.onClick?.(event);
                    }}
                  >
                    {postMenuItem?.render(optionsData)}
                  </Stack>
                )}
              </Paper>
            );
          }}
          renderInput={(params) => {
            return (
              <TextField
                {...params}
                label={label}
                InputProps={{
                  ...params.InputProps,
                  ...autoCompleteInputProps?.(params),
                }}
                {...autoCompleteTextFieldProps}
              />
            );
          }}
        />
      );
    }
    return (
      <Select
        {...rest}
        postMenuItem={postMenuItem}
        label={label}
        preMenuItem={preMenuItem}
        options={optionsData}
        disabled={loading || isDisabled}
        selectProps={{
          sx: {
            '.MuiSelect-icon': {
              visibility: loading ? 'hidden' : 'visible',
            },
          },
          renderValue: options?.renderDisplay ?? undefined,
          endAdornment: (
            <Box
              display={'flex'}
              visibility={loading ? 'visible' : 'hidden'}
              justifyContent={'center'}
              alignItems={'center'}
              textAlign={'center'}
            >
              <CircularProgress
                size={15}
                sx={{
                  display: 'flex',
                  alignSelf: 'center',
                }}
              />
            </Box>
          ),
          ...selectProps,
        }}
      />
    );
  };

  // ======================= VIEWS
  return renderSelectType();
};

export type SelectWithDataOptionsProps = OptionsProps;
export const SelectWithData = SelectWithDataView;
export default SelectWithDataView;
